import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import { first, map, shareReplay, switchMap, tap, catchError } from 'rxjs/operators';
import { COLLECTIONS } from '../core.constants';
import { Id, Role } from '../models/common.model';
import { User } from '../models/user.model';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { UserPreferencesService } from '../use-case-services/user-preferences.service';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private cachedUser: Record<Id, Observable<string>> = {};
  private cachedDisplayNames: Record<Id, Observable<string>> = {};
  user$: Observable<User> = this.auth.uid$.pipe(
    switchMap((uid) => this.firestore.getDocument<User>(COLLECTIONS.USERS, uid)),
    map((user) => {
      user.imageId = user.imageId || '/assets/icon/default-avatar.png';
      return user;
    }),
    tap((user) => {
      Sentry.setUser({ email: user.email, id: user.id });
      Sentry.setContext('role', { roles: user.roles });
      this.userPreferencesService.myPreferences$.pipe(first()).subscribe();
    }),
    shareReplay(1),
  );

  uid$: Observable<Id> = this.auth.uid$;

  roles$: Observable<Role[]> = this.user$.pipe(
    map((u) => u.roles || []),
    shareReplay(1),
  );

  public isAdmin$: Observable<boolean> = this.roles$.pipe(map((roles) => roles.indexOf('admin') > -1));
  public isEmployee$: Observable<boolean> = this.roles$.pipe(map((roles) => roles.indexOf('employee') > -1));
  public isBooking$: Observable<boolean> = this.roles$.pipe(map((roles) => roles.indexOf('booking') > -1));
  public isMedia$: Observable<boolean> = this.roles$.pipe(map((roles) => roles.indexOf('media') > -1));
  public isOrganization$: Observable<boolean> = this.roles$.pipe(map((roles) => roles.indexOf('organization') > -1));
  public isMechanic$: Observable<boolean> = this.roles$.pipe(map((roles) => roles.indexOf('mechanic') > -1));

  constructor(
    private firestore: FirestoreService,
    private userPreferencesService: UserPreferencesService,
    private translocoService: TranslocoService,
    private auth: AuthService,
  ) {}

  updateUserData(userData: Partial<User>): Promise<void> {
    return firstValueFrom(
      this.uid$.pipe(
        first(),
        switchMap((uid) => this.firestore.createOrUpdate(COLLECTIONS.USERS, uid, userData)),
      ),
    );
  }

  updateUserPhoto(userData: Partial<User>, targetUserId?: string): Promise<void> {
    return firstValueFrom(
      this.uid$.pipe(
        first(),
        switchMap((uid) => {
          const userId = targetUserId || uid;
          return this.firestore.createOrUpdate(
            [COLLECTIONS.USERS, userId, COLLECTIONS.USERS_PHOTOS].join('/'),
            userId,
            userData,
          );
        }),
      ),
    );
  }

  createUser(email: string, pass: string, userData?: any) {
    const lang = this.translocoService.getActiveLang();
    return from(this.auth.register(email, pass)).pipe(
      map((res) => res.user.uid),
      switchMap(async (uid) => {
        await this.userPreferencesService.createOrUpdatePreferences({
          language: lang,
          messagesLanguage: lang,
          emails: true,
          notifications: true,
        });
        return uid;
      }),
      switchMap((id) => this.firestore.create(COLLECTIONS.USERS, { id, email, ...userData })),
    );
  }

  getUserEmail(userId: string): Observable<string> {
    if (!this.cachedUser[userId]) {
      this.cachedUser[userId] = this.firestore
        .getDocument<User>(COLLECTIONS.USERS, userId)
        .pipe(map((res) => res.email));
    }
    return this.cachedUser[userId];
  }

  getUserDisplayName(userId: string): Observable<string> {
    if (!this.cachedDisplayNames[userId]) {
      this.cachedDisplayNames[userId] = this.firestore.getDocument<User>(COLLECTIONS.USERS, userId).pipe(
        map((user) => user.email || userId),
        catchError(() => of(userId)),
        shareReplay(1),
      );
    }
    return this.cachedDisplayNames[userId];
  }

  userExists(uid: string) {
    return this.firestore.exists(COLLECTIONS.USERS, uid);
  }
}
